import get from "lodash/get"
import { cleanedText, urlHelper } from "../util"
import getLocalizedString from "./getLocalizedString"

/**
 * @typedef getLocalizedItemTypeProps
 * @property {Object} item - a content item
 * @property {Object[]} localizedStrings - localization string items
 */

/**
 * Gets an item’s type in the current language
 * @param {getLocalizedItemTypeProps} props
 * @returns {string}
 */
export const getLocalizedItemType = ({ item, localizedStrings }) => {
  const localizedCaseStudy = getLocalizedString(
    localizedStrings,
    "localization_string___case_study",
    "Case Study"
  )

  const localizedInsights = getLocalizedString(
    localizedStrings,
    "localization_string___insights",
    "Insights"
  )

  const localizedNews = getLocalizedString(
    localizedStrings,
    "localization_string___news",
    "News"
  )

  const localizedSpotlight = getLocalizedString(
    localizedStrings,
    "localization_string___spotlight",
    "Spotlight"
  )
  const typeCodename =
    get(item, "elements.article_type.value[0].codename") ||
    get(item, "system.type") ||
    "article"

  const type = {
    article: localizedNews,
    news: localizedNews,
    post: localizedInsights,
    spotlight: localizedSpotlight,
    case_study: localizedCaseStudy,
  }[typeCodename]

  return type
}

export const articleTransformer = (item, { localizedStrings }) => {
  const type = getLocalizedItemType({ item, localizedStrings })

  return {
    title: get(item, "elements.title.value"),
    url: get(item, "resolved_url"),
    summary: get(item, "elements.summary.value"),
    image: imageItemTransformer(get(item, "elements.featured_media.value[0]")),
    postDate: get(item, "elements.post_date.value"),
    pinned: get(item, "pinned"),
    typeCodename:
      get(item, "elements.article_type.value[0].codename") ||
      get(item, "system.type") ||
      "article",
    type: type,
  }
}

export const billboardV2Transformer = (item) => {
  const preferredRatio =
    {
      n16_9: "56.25vw",
      n16_4_5: "28.125vw",
    }[get(item, "elements.preferred_ratio.value[0].codename")] || "56.25vw"

  const textWidth =
    {
      full: "full",
      n80: "4/5",
      n75: "3/4",
      n66: "2/3",
      n60: "3/5",
      n50: "1/2",
    }[get(item, "elements.text_width.value[0].codename")] || "3/5"

  return {
    appendChildren: get(item, "appendChildren"),
    divider: get(item, "elements.divider.value[0].codename"),
    eyebrow: get(item, "elements.eyebrow.value"),
    headline: get(item, "elements.headline.value"),
    headlineSize: get(item, "elements.headline_size.value[0].codename"),
    media:
      get(item, "elements.background_media.value[0].elements.image.value[0]") ||
      get(item, "elements.background_media.value[0]"),
    mediaType: get(item, "elements.background_media.value[0].system.type"),
    preferredRatio,
    text: get(item, "elements.text.value"),
    textAlignment:
      get(item, "elements.text_alignment.value[0].codename") || "center",
    textLinkedItems: get(item, "elements.text.modular_content"),
    textLinks: get(item, "elements.text.links"),
    // textPosition is replaced in the model by textAlignment and textVerticalPosition,
    // and can be removed as soon as this work is deployed
    textPosition: get(item, "elements.text_position.value[0].codename"),
    textVerticalPosition:
      get(item, "elements.text_vertical_position.value[0].codename") ||
      "middle",
    textWidth,
  }
}

export const breadcrumbTransformer = (item) => {
  return {
    url: get(item, "resolved_url", ""),
    title: get(item, "elements.title.value", ""),
    type: get(item, "system.type", ""),
    codename: get(item, "system.codename", ""),
  }
}

export const caseStudyLinkCardTransformer = (item) => {
  return {
    company: get(item, "elements.company.value[0].elements.company_name.value"),
    linkTo: item.resolved_url,
    id: get(item, "system.id"),
    heading: get(item, "elements.seo_metadata__open_graph_title.value"),
    description: get(
      item,
      "elements.seo_metadata__open_graph_description.value"
    ),
    image: imageItemTransformer(
      get(item, "elements.seo_metadata__seo_image.value[0]")
    ),
    featured: get(item, "elements.featured.value[0].codename"),
    industries: item?.elements?.industries?.value
      .filter((industry) => industry)
      .map((industryEntry) => {
        return {
          title: industryEntry?.elements?.title?.value,
          codename: industryEntry?.system?.codename,
          id: industryEntry?.system?.id,
          type: "industry",
          url_slug: industryEntry?.elements?.url_slug?.value,
        }
      }),
    productGroups: item?.elements?.product_groups?.value
      .filter((product_group) => product_group)
      .map((productGroupEntry) => {
        return {
          title: productGroupEntry?.elements?.title?.value,
          codename: productGroupEntry?.system?.codename,
          id: productGroupEntry?.system?.id,
          type: "product_group",
          url_slug: productGroupEntry?.elements?.url_slug?.value,
        }
      }),
    servicesSolutions: item?.elements?.services_solutions?.value
      .filter((service_solution) => service_solution)
      .map((ServiceSolutionEntry) => {
        return {
          title: ServiceSolutionEntry?.elements?.title?.value,
          codename: ServiceSolutionEntry?.system?.codename,
          id: ServiceSolutionEntry?.system?.id,
          type: "service_solution",
          url_slug: ServiceSolutionEntry?.elements?.url_slug?.value,
        }
      }),
  }
}

export const columnsTransformer = (item) => {
  const includeDivider = item.elements.options.value.some(
    (option) => option["codename"] === "include_divider"
  )
  const reverseDirectionOnSmallScreens = item.elements.options.value.some(
    (option) => option["codename"] === "sm_reverse_columns"
  )

  const columnWidths =
    {
      n2_col_2_5_and_3_5: "2/5 and 3/5",
      n2_col_3_5_and_2_5: "3/5 and 2/5",
    }[get(item, "elements.column_widths.value[0].codename")] || "equal"

  const itemsPerRow = {
    n1: 1,
    n2: 2,
    n3: 3,
    n4: 4,
  }[get(item, "elements.items_per_row.value[0].codename")]

  return {
    columns: get(item, "elements.columns.value"),
    columnWidths,
    itemsPerRow,
    includeDivider,
    reverseDirectionOnSmallScreens,
  }
}

export const dividerTransformer = (item) => {
  const spacing = get(item, "elements.spacing.value[0].codename")

  return {
    spacing: spacing === "default" ? undefined : spacing,
    style: get(item, "elements.style.value[0].codename"),
  }
}

export const floatingActionButtonTransformer = (item) => {
  const icon =
    {
      mail: "icon-mail",
      message: "icon-message-square",
      phone: "icon-call",
    }[get(item, "elements.icon.value[0].codename")] || "icon-call"

  return {
    heading: get(item, "elements.heading.value"),
    text: get(item, "elements.text.value"),
    linkTo: urlHelper(get(item, "elements.link_to.value[0]")),
    icon,
  }
}

export const headingTransformer = (item) => {
  // Kentico adds an `n` before values that start with a number (e.g `n2xl` instead of `2xl`) so those must be mapped back to numbers.
  // Otherwise, use the value provided by Kentico (e.g. `xl`, `lg`)
  const size =
    {
      n2xl: "2xl",
    }[get(item, "elements.size.value[0].codename")] ||
    get(item, "elements.size.value[0].codename")

  const headingLevel =
    {
      paragraph: "p",
    }[get(item, "elements.heading_level.value[0].codename")] ||
    get(item, "elements.heading_level.value[0].codename")
  // TODO: Add all widths and/or widths-specific transformer
  const width =
    {
      full: "full",
      n80: "4/5",
      n75: "3/4",
      n66: "2/3",
      n50: "1/2",
    }[get(item, "elements.width.value[0].codename")] || undefined

  const color = get(item, "elements.color.value[0].codename")

  return {
    color: color === "default" ? undefined : color,
    eyebrow: get(item, "elements.eyebrow.value"),
    headingLevel: headingLevel || "h2",
    includeDivider:
      get(item, "elements.include_divider.value[0].codename") === "yes",
    size: size,
    text: get(item, "elements.text.value"),
    transform: get(item, "elements.transform.value[0].codename"),
    width: width,
  }
}

export const inlineAsideTransformer = (item) => ({
  html: get(item, "elements.text.value"),
  htmlLinkedItems: get(item, "elements.text.modular_content"),
  htmlLinks: get(item, "elements.text.links"),
})

export const imageItemTransformer = (item) => {
  const focalPoint = {
    left_top: "left-top",
    top: "top",
    right_top: "right-top",
    left: "left",
    center: "center",
    right: "right",
    left_bottom: "left-bottom",
    bottom: "bottom",
    right_bottom: "right-bottom",
  }[get(item, "elements.focal_point.value[0].codename") || "center"]

  return {
    alt: get(item, "elements.image__alt_text.value"),
    height: get(item, "elements.image__asset.value[0].height"),
    url: get(item, "elements.image__asset.value[0].url"),
    width: get(item, "elements.image__asset.value[0].width"),
    caption: cleanedText(get(item, "elements.caption.value")),
    captionLinkedItems: get(item, "elements.caption.modular_content"),
    captionLinks: get(item, "elements.caption.links"),
    focalPoint: focalPoint,
  }
}

export const linkCardTransformer = (item) => {
  const image = get(item, "elements.image.value[0]")
  const linkTo = get(item, "elements.link_to.value[0]")
  const linkCardColorScheme = get(
    item,
    "elements.color_scheme.value[0].codename"
  )

  return {
    description: get(item, "elements.description.value"),
    heading: get(item, "elements.heading.value"),
    id: get(item, "system.id"),
    image: image ? imageItemTransformer(image) : null,
    linkTo: linkTo && urlHelper(linkTo),
    linkCardColorScheme: linkCardColorScheme ? linkCardColorScheme : "light",
  }
}

export const linkCardsTransformer = (item) => {
  const linkCards = get(item, "elements.link_cards.value")
  const transformedLinkCards = []

  if (!linkCards) {
    return
  }

  linkCards.forEach((linkCard) =>
    transformedLinkCards.push(linkCardTransformer(linkCard))
  )

  return transformedLinkCards
}

export const industryLinkCardTransformer = (item) => {
  const image =
    get(item, "elements.listing_image.value[0]") ||
    get(item, "elements.seo_metadata__seo_image.value[0]")
  const linkCardColorScheme = get(
    item,
    "elements.color_scheme.value[0].codename"
  )

  return {
    description: get(item, "elements.description.value", ""),
    heading: get(item, "elements.title.value"),
    headingColor: "blue",
    id: get(item, "system.id"),
    image: image ? imageItemTransformer(image) : null,
    linkTo: get(item, "resolved_url"),
    linkCardColorScheme: linkCardColorScheme ? linkCardColorScheme : "light",
  }
}

export const solutionTransformer = (item) => {
  const linkTo = get(item, "elements.link_to.value[0].resolved_url")

  return {
    eyebrow: get(item, "elements.title.value"),
    analyticsTitle: get(item, "system.name"),
    heading: get(item, "elements.headline.value"),
    featuredMedia: get(item, "elements.featured_media.value[0]"),
    id: get(item, "system.id"),
    overview: get(item, "elements.overview.value"),
    overviewLinkedItems: get(item, "elements.overview.modular_content"),
    overviewLinks: get(item, "elements.overview.links"),
    urlSlug: get(item, "elements.url_slug.value"),
    link: linkTo
      ? {
          label: get(item, "elements.link_label.value"),
          to: linkTo,
          "data-analytics-title": get(
            item,
            "elements.link_to.value[0].system.name"
          ),
        }
      : undefined,
    differentiator: get(item, "elements.differentiator.value[0]"),
    differentiatorHeading: get(
      item,
      "elements.differentiator_headline.value[0].elements.localized_string_value.value"
    ),
  }
}

export const linkDescriptionTransformer = (item) => {
  const image = get(item, "elements.image.value[0]")
  const link_appearance = get(
    item,
    "elements.link_appearance.value[0].codename"
  )

  return {
    heading: get(item, "elements.headline.value"),
    image: image ? imageItemTransformer(image) : null,
    linkAppearance: link_appearance ? link_appearance : "more",
    linkLabel: get(item, "elements.link_label.value"),
    linkTo: get(item, "elements.link_to.value[0].resolved_url"),
    openInNewWindow:
      get(item, "elements.open_in_new_window.value[0].codename") === "yes"
        ? true
        : false,
    text: cleanedText(get(item, "elements.text.value")),
    textLinks: get(item, "elements.text.links"),
  }
}

export const linkItemTransformer = (item) => {
  return {
    label: get(item, "elements.label.value"),
    to: urlHelper(get(item, "elements.link_to.value[0]")),
    style: get(item, "elements.style.value[0].codename"),
  }
}

export const localNavItemTransformer = (item) => {
  const children =
    get(
      item,
      "elements.meta__include_children_in_navigation.value[0].codename"
    ) === "yes" &&
    item.child_items &&
    item.child_items.length
      ? item.child_items.map((child) => localNavItemTransformer(child))
      : null

  return {
    codename: get(item, "system.codename"),
    id: get(item, "system.id"),
    label:
      get(item, "elements.meta__nav_title.value") ||
      get(item, "elements.title.value"),
    url: get(item, "resolved_url"),
    type: get(item, "system.type"),
    order: get(item, "elements.meta__sort_order.value", 99),
    hideFromNav:
      get(item, "elements.meta__hide_from_navigation.value[0].codename") ===
      "yes",
    children: children || [],
  }
}

export const localNavLinksTransformer = ({ item, localizedStrings }) => {
  const links = []

  const localizedBeltTypes = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___belt_types",
    "Belt Types"
  )
  const localizedEquipmentTypes = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___equipment_types",
    "Equipment Types"
  )
  const localizedProductTypes = getLocalizedString(
    localizedStrings.nodes,
    "localization_string___product_types",
    "Product Types"
  )

  const groupLabel = {
    modular_plastic_belting: localizedBeltTypes,
    thermodrive: localizedBeltTypes,
    aim: localizedEquipmentTypes,
    spirals: localizedProductTypes,
    arb_equipment: localizedEquipmentTypes,
  }

  if (!item) {
    return
  }

  const topNav = { ...localNavItemTransformer(item) }

  // Top Link, add to the start of array
  links.unshift(topNav)

  if (item.system.type === "product_group") {
    const productTypes = get(item, "elements.link_cards.value", []).map(
      (linkCard) => {
        return localNavItemTransformer(
          get(linkCard, "elements.link_to.value[0]")
        )
      }
    )

    if (productTypes && productTypes.length) {
      links.push({
        label: (topNav && groupLabel[topNav.codename]) || "Fallback",
        url: get(productTypes, "[0].url"),
        id: get(productTypes, "[0].id"),
        type: `button`,
        order: 1, // position after top nav
        children: productTypes,
      })
    }
  }

  /*
   * Filter child links to remove items that are already in nav and product applications
   */
  const children = item.child_items
    .map((child) => {
      return localNavItemTransformer(child)
    })
    .filter((child) => {
      /*
       * Determines whether a link with the same URL already exists in the array of links (also checks recursively)
       */
      const linkIsInNav = ({ link, navLinks }) => {
        return navLinks.some(
          (navItem) =>
            link.url === navItem.url ||
            (navItem.children &&
              navItem.children.length &&
              linkIsInNav({ link: link, navLinks: navItem.children }))
        )
      }
      return (
        !linkIsInNav({ link: child, navLinks: links }) &&
        child.hideFromNav !== true
      )
    })

  links.push(...children)
  return links
}

export const mediaTileTransformer = (item) => {
  const mediaRatio =
    {
      n1_1: 1,
      n6_4: 0.666,
      n4_6: 1.5,
    }[get(item, "elements.media_ratio.value[0].codename")] || 1

  const stretchMedia = {
    yes: true,
    no: false,
  }[get(item, "elements.stretch_media.value[0].codename") || "yes"]

  return {
    eyebrow: get(item, "elements.eyebrow.value"),
    headline: get(item, "elements.headline.value"),
    media: get(item, "elements.media.value[0]"),
    mediaPosition: get(item, "elements.media_position.value[0].codename"),
    mediaWidth: get(item, "elements.media_width.value[0].codename") || "n50",
    mediaRatio: mediaRatio,
    stretchMedia: stretchMedia,
    text: get(item, "elements.text.value"),
    textLinkedItems: get(item, "elements.text.modular_content"),
    textLinks: get(item, "elements.text.links"),
    linkTo: get(item, "elements.link_to.value[0]"),
    linkLabel: get(item, "elements.link_label.value"),
  }
}

export const navigationItemTransformer = (item) => {
  const children = (children) =>
    children.map((child) => navigationItemTransformer(child))

  return {
    id: get(item, "system.id"),
    label: get(item, "elements.label.value"),
    linkTo: urlHelper(get(item, "elements.link_to.value[0]")),
    children: children(get(item, "elements.children.value") || []),
  }
}

export const seoStructuredDataTransformer = (item) => {
  const bodyBlocks = get(item, "elements.body_blocks__body_blocks.value") || []

  const entitiesSchema = []

  const faqSchemaTransformer = ({ elements }) => {
    const question = get(elements, "question.value")
    const answer = get(elements, "answer.value")
    return {
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
      },
    }
  }

  // check if this a BodyBlocks item, or a standalone piece of content
  if (bodyBlocks.length >= 1) {
    bodyBlocks.forEach((block) => {
      const { type } = block.system

      switch (type) {
        case "faq_block":
          block.elements.faqs.value.forEach((faq) => {
            entitiesSchema.push(faqSchemaTransformer(faq))
          })
          break
        default:
          break
      }
    })
  } else {
    const standaloneFaqs =
      get(item, "elements.frequently_asked_questions.value") || []

    standaloneFaqs.map((faq) => {
      return entitiesSchema.push(faqSchemaTransformer(faq))
    })
  }

  return entitiesSchema
}

export const seoMetaTransformer = (item, global) => {
  const title =
    get(item, "elements.seo_metadata__meta_title.value") ||
    get(item, "elements.title.value")
  const codename = get(item, "system.codename")
  const description =
    get(item, "elements.seo_metadata__meta_description.value") ||
    get(global, "elements.seo_description.value")
  const seoImage =
    get(item, "elements.seo_metadata__seo_image.value[0]") ||
    get(global, "elements.seo_image.value[0]")
  const systemType = get(item, "system.type")

  return {
    description: description,
    image: get(seoImage, "elements.image__asset.value[0].url"),
    imageAltText: get(seoImage, "elements.image__alt_text.value"),
    lang: get(item, "system.language"),
    openGraphTitle:
      get(item, "elements.seo_metadata__open_graph_title.value") || title,
    openGraphDescription:
      get(item, "elements.seo_metadata__open_graph_description.value") ||
      description,
    title: title,
    type: codename ? codename : systemType,
    ...(item.resolved_url && {
      canonicalUrl: `https://www.intralox.com${item.resolved_url}`,
    }),
  }
}

export const quickLinkTransformer = (item) => {
  const image = get(item, "elements.image.value[0]")
  const linkItem = get(item, "elements.link_to.value[0]")
  const icon = {
    mail: "mail",
    message: "message-square",
    phone: "call",
  }[get(item, "elements.icon.value[0].codename")]

  return {
    heading: get(item, "elements.heading.value"),
    text: get(item, "elements.text.value"),
    icon: image ? imageItemTransformer(image) : icon,
    linkTo: linkItem && urlHelper(linkItem),
    linkTarget:
      get(item, "elements.open_in_new_window.value") === "true"
        ? "_blank"
        : undefined,
  }
}

export const relatedItemLinkCardTransformer = (item) => {
  switch (item?.system?.type) {
    // If the item is a link card, use the link card transformer
    case "link_card":
      return linkCardTransformer(item)
    // Otherwise, transform the item into the shape of a link card
    default:
      return {
        image: imageItemTransformer(
          get(item, "elements.seo_metadata__seo_image.value[0]")
        ),
        heading:
          get(item, "elements.seo_metadata__open_graph_title.value") ||
          get(item, "elements.seo_metadata__meta_title.value") ||
          get(item, "elements.headline.value"),
        description:
          get(item, "elements.seo_metadata__open_graph_description.value") ||
          get(item, "elements.seo_metadata__meta_description.value"),
        linkTo: item?.resolved_url,
        id: get(item, "system.id"),
        label: get(
          item,
          "elements.company.value[0].elements.company_name.value"
        ),
      }
  }
}

export const relatedItemsTransformer = ({ item, localizedStrings }) => {
  let items =
    get(item, "elements.related_items.value") ||
    get(item, "elements.related_content.value") ||
    []

  if (items.includes(null)) {
    if (typeof window === "undefined") {
      console.error(
        "One of the pieces of content that is being queried may be archived."
      )
    }
  }

  items = items.filter((item) => item)

  return {
    headline: get(
      item,
      "elements.related_items_headline.value[0].elements.localized_string_value.value"
    ),
    overview: get(item, "elements.related_items_overview"),
    items: items.map((item) => ({
      ...relatedItemLinkCardTransformer(item),
      ...{
        label: getLocalizedItemType({
          item:
            item.system?.type === "link_card"
              ? item.elements?.link_to.value?.[0]
              : item,
          localizedStrings: localizedStrings,
        }),
      },
    })),
  }
}

export const sectionLinkCardTransformer = (item) => {
  const childItems = get(item, "child_items", [])
  const links = []

  childItems.forEach((item) => {
    links.push({
      id: get(item, "id"),
      linkTo: get(item, "resolved_url"),
      label: get(item, "elements.title.value"),
      analyticsTitle: get(item, "system.name"),
    })
  })
  return {
    label: get(item, "elements.title.value"),
    analyticsTitle: get(item, "system.name"),
    linkTo: get(item, "resolved_url"),
    description: get(item, "elements.description.value", ""),
    image: imageItemTransformer(get(item, "elements.listing_image.value[0]")),
    links,
  }
}

export const testimonialTransformer = (item) => {
  const localizedLinkLabel = get(
    item,
    "elements.localized_link_label.value[0].elements.localized_string_value.value"
  )
  const customLinkLabel = get(item, "elements.custom_link_label.value")
  const linkLabel = customLinkLabel
    ? customLinkLabel
    : localizedLinkLabel
    ? localizedLinkLabel
    : null
  return {
    quote: get(item, "elements.quote.value"),
    attribution: get(item, "elements.attribution.value"),
    image: get(item, "elements.image.value[0]"),
    jobTitle: get(item, "elements.job_title.value"),
    language: get(item, "system.language"),
    linkLabel: linkLabel,
    linkTo: get(item, "elements.link_to.value[0].resolved_url"),
    linkAttr: {
      "data-analytics-title": get(
        item,
        "elements.link_to.value[0].system.name"
      ),
    },
    position: get(item, "elements.position.value[0].codename"),
  }
}
